import { useQueries, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { budgetsQuery, meQuery } from '../api';
import { needsSetupQuery } from '../api/homes';
import { letterCoverageQuery } from '../api/letter-coverage';
import { settingQuery } from '../api/settings';
import { formatPerShare } from '../settings/model';
import { ChangedLine, useMayRead, useSetting } from '../settings/parts';
import { Card, Icon, cx, formatCount, formatDate, plural } from '../ui';
import { AWARD_TYPE_LABEL } from './labels';
import { useCanOpenPage } from './pageLinks';
import { SetupSteps, type SetupStepState } from './SetupSteps';
import { remindersText } from './ValuesInUseCard';
import s from './Home.module.css';

const TYPES = ['SDB', 'EDB', 'PAA', 'TAA'] as const;
const LETTERS_HREF = '/settings/elections-letters';
const PEOPLE_HREF = '/settings/system/people';

/**
 * FR4 · "Ready for the first award?": what an award needs before it reaches the Stock Admin
 * team, from the real state: a letter in use for each signer and mix (APP-130), people with their
 * app-owned directory fields, the FMV, each pool's OH and G&A projects, and the election window.
 * Done items fold away, and the card goes once everything is ready (DESIGN-SPEC §5).
 */
export function ReadinessCard({
  fiscalYear,
  firstDay,
}: {
  fiscalYear: number;
  /** Nothing has reached the queue yet: the old tracker's import is still worth offering. */
  firstDay: boolean;
}): ReactElement | null {
  const { data: me } = useSuspenseQuery(meQuery);
  // Offered only once Settings › System administration › People has landed and to people who may open it (System Admins).
  const canOpenPeople = useCanOpenPage(me, PEOPLE_HREF);
  // APP-130: letters are ready when every cell of the coverage grid is (a signer × mix).
  const mayReadLetters = useMayRead('letters.templates');
  const coverage = useQuery({ ...letterCoverageQuery, enabled: mayReadLetters === true });
  // New people arrive with every directory sync: once awards flow, Settings › System administration › People keeps them.
  const needsSetup = useQuery({ ...needsSetupQuery, enabled: firstDay });
  const positions = useQuery(budgetsQuery);
  const fmv = useSetting('values.fmvCents');
  const electionWindow = useSetting('election.windowDays', 'PAA');
  const reminders = useSetting('election.reminderDaysBefore', 'PAA');
  // The pools with projects of their own: not PAA/TAA, nor the Spot Awards pool (APP-264).
  const discretionary = (positions.data?.pools ?? []).filter(
    (p) => p.kind === 'sector' || p.kind === 'executive',
  );
  const projects = useQueries({
    queries: discretionary.map((p) => settingQuery('coding.poolProjects', p.pool.id)),
  });

  const steps: SetupStepState[] = [];

  if (coverage.isSuccess) {
    // APP-130: an award type is ready once every signer has a letter in use for every cash and
    // options mix its awards can have. Hidden mixes never count; a signer that can't be found does.
    const grids = coverage.data.grids.filter((g) =>
      (TYPES as readonly string[]).includes(g.awardType),
    );
    const ready = grids.reduce((n, g) => n + g.counts.ready, 0);
    const total = grids.reduce((n, g) => n + g.counts.total, 0);
    // APP-212: letters the built-in sample fills are never ready, and the card says how many.
    const sample = grids.reduce((n, g) => n + g.counts.sample, 0);
    // APP-221: cells that need no letter aren't in the total; the card says them apart.
    const notNeeded = grids.reduce((n, g) => n + g.counts.notNeeded, 0);
    const canEdit = grids.some((g) => g.canEdit);
    steps.push({
      id: 'templates',
      done: ready === total,
      title: 'Letter templates',
      status:
        ready === 0
          ? 'To do'
          : `In progress · ${formatCount(ready)} of ${plural(total, 'letter', 'letters')} ready${sample > 0 ? ` · ${formatCount(sample)} still the sample` : ''}${notNeeded > 0 ? ` · ${formatCount(notNeeded)} not needed` : ''}`,
      summary: 'one for each signer and cash and options mix',
      body: 'Word, with the signature already in it: upload each into its cell, check its sample, then approve and use it. A letter can’t go out until its signer and mix have one in use.',
      detail: (
        <ul className={s.templateGrid} aria-label="Letter templates by award type">
          {TYPES.map((type) => {
            const grid = grids.find((g) => g.awardType === type);
            const missing = grid ? grid.counts.total - grid.counts.ready : 0;
            const ok = grid !== undefined && missing === 0;
            return (
              <li key={type} className={cx(s.templateTile, !ok && s.templateMissing)}>
                <Icon name={ok ? 'check' : 'upload'} size={18} />
                <span className={s.templateText}>
                  <span className={s.strong}>{AWARD_TYPE_LABEL[type]}</span>
                  <span className={s.meta}>
                    {grid === undefined
                      ? 'Not shown to you'
                      : grid.counts.total === 0
                        ? 'None needed yet'
                        : ok
                          ? 'Ready'
                          : `${plural(missing, 'letter', 'letters')} to finish`}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      ),
      action: canEdit ? { label: 'Upload templates', href: LETTERS_HREF } : null,
    });
  }

  if (firstDay && needsSetup.isSuccess) {
    const count = needsSetup.data;
    steps.push({
      id: 'directory',
      done: count === 0,
      title:
        count === 0
          ? 'Directory'
          : `Directory: ${plural(count, 'person needs', 'people need')} setting up`,
      status: count === 0 ? null : 'To do',
      summary: 'everyone has a sector, home org and OH/G&A',
      body: canOpenPeople
        ? 'New from Entra ID with no sector, home org or OH/G&A. Without them, a line can’t find its Sector Lead, pool or charge code. Fill them in on each person, or from your G&A list by CSV.'
        : 'New from Entra ID with no sector, home org or OH/G&A. Without them, a line can’t find its Sector Lead, pool or charge code. A System Admin fills them in under Settings › System administration › People, on each person or from the G&A list by CSV.',
      action:
        count > 0 && canOpenPeople
          ? { label: 'Set them up', href: `${PEOPLE_HREF}?status=needs_setup` }
          : null,
    });
  }

  if (fmv.status === 'ready') {
    const value = typeof fmv.view.value === 'number' ? formatPerShare(fmv.view.value) : null;
    steps.push({
      id: 'fmv',
      done: fmv.view.lastChanged !== null,
      title: 'FMV per share',
      status: 'To do',
      summary:
        value === null
          ? null
          : `${value}${fmv.view.effectiveFrom ? ` from ${formatDate(fmv.view.effectiveFrom)}` : ''}`,
      body: 'The fair market value every award’s options are worked out from.',
      changed: <ChangedLine change={fmv.view.lastChanged} />,
      action: { label: 'Set the FMV', href: '/settings/values' },
    });
  }

  const projectViews = projects.map((q) => q.data);
  if (discretionary.length > 0 && projectViews.every((v) => v !== undefined)) {
    const missing = discretionary.filter((_, i) => projectViews[i]?.value == null);
    steps.push({
      id: 'charge-codes',
      done: missing.length === 0,
      title: 'Charge codes',
      status: `${formatCount(missing.length)} ${missing.length === 1 ? 'pool' : 'pools'} without projects`,
      summary: 'OH and G&A projects for every pool',
      body: `Every discretionary pool needs its OH and G&A accounting projects before its lines can be coded. Missing: ${missing.map((p) => p.pool.name).join(', ')}.`,
      action: { label: 'Set charge codes', href: '/settings/charge-codes' },
    });
  }

  if (electionWindow.status === 'ready') {
    const days = typeof electionWindow.view.value === 'number' ? electionWindow.view.value : null;
    steps.push({
      id: 'elections',
      done: days !== null,
      title: 'Election window and reminders',
      status: 'To do',
      summary: [
        days === null ? null : plural(days, 'day', 'days'),
        reminders.status === 'ready' ? remindersText(reminders.view.value).toLowerCase() : null,
      ]
        .filter((part): part is string => part !== null)
        .join(' · '),
      body: 'How long an employee has to choose cash or stock, and when they’re reminded.',
      action: { label: 'Set the window', href: LETTERS_HREF },
    });
  }

  if (firstDay) {
    steps.push({
      id: 'import',
      done: false,
      optional: true,
      title: `Past awards: import the ${String(fiscalYear)} tracker`,
      body: `Bring this year’s awards in from the old tracker, so budgets left, past awards and recognition history are right from day one. A System Admin runs the import as one audited batch.`,
    });
  }

  const required = steps.filter((step) => step.optional !== true);
  const doneCount = required.filter((step) => step.done).length;
  if (required.length === 0 || doneCount === required.length) return null;

  return (
    <Card
      title={firstDay ? 'Ready for the first award?' : 'Your setup'}
      accent="cyan"
      help={{
        shows: 'What an award needs before it reaches your queue, from what’s set up today.',
        canDo:
          'Open each item to finish it. Done items fold away, and this list goes once everything is ready.',
      }}
    >
      <SetupSteps
        label="Your setup"
        steps={steps}
        doneCount={doneCount}
        requiredCount={required.length}
        footer="Done items fold away. This list goes once everything is ready; each item lives under Settings after that."
      />
    </Card>
  );
}
